// The Windows commands (short Windows install command spec §3.1, §3.3). The default is typed into the
// user's own PowerShell and fetches the channel bootstrap from `/win`. Values are checked against these
// sets rather than escaped, so a command never holds a `$`, backtick or double quote.
/* eslint-disable i18next/no-literal-string -- every string here is shell syntax the user pastes into a terminal, or a
   guard error for a value the control plane never issues; none of it is interface copy. */
const CLAIM_TOKEN = /^[A-Za-z0-9_-]+$/u;
const DNS_NAME = /^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*$/u;

export type WindowsShell = 'powershell' | 'cmd';

function checked(value: string, pattern: RegExp, what: string): string {
  if (!pattern.test(value)) throw new Error(`The ${what} cannot be put in a Windows command.`);
  return value;
}

function installArguments(claimToken: string, zoneHost?: string | null): string {
  const firm = zoneHost === undefined || zoneHost === null ? '' : ` -Topology firm -ZoneHost '${checked(zoneHost, DNS_NAME, 'zone host')}'`;
  return `-Claim '${checked(claimToken, CLAIM_TOKEN, 'claim token')}'${firm}`;
}

function powershellCommand(installer: string, args: string): string {
  return `irm https://${checked(installer, DNS_NAME, 'installer host')}/win|iex;Install-LexSide ${args}`;
}

function utf16LeBase64(value: string): string {
  let binary = '';
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    binary += String.fromCharCode(code & 0xff, code >>> 8);
  }
  return btoa(binary);
}

// The Command Prompt tab: the bootstrap travels base64 encoded, because a double-quoted -Command in
// one shell is expanded by the other (Windows installation spec §3.4). It is its own process, so it
// exits with the installer's code.
function cmdCommand(argument: string, endpoints: {china: boolean;installer: string}): string {
  const overrides=endpoints.china?[
    "$env:LEXSIDE_CONTROL_URL = 'https://api.cn.lexside.ai'",
    "$env:LEXSIDE_APP_ORIGIN = 'https://cn.lexside.ai'",
    "$env:LEXSIDE_RELEASE_BASE_URL = 'https://dl.cn.lexside.ai/releases'",
  ]:[];
  const bootstrap = [
    ...overrides,
    "$f = Join-Path $env:TEMP ('lexside-install-' + [guid]::NewGuid().ToString('N') + '.ps1')",
    'try {',
    // Best-effort revocation when curl accepts it (revocation spec §3.2).
    '  $revoke = @()',
    "  & curl.exe --ssl-revoke-best-effort --version 1>$null 2>$null",
    "  if ($LASTEXITCODE -eq 0) { $revoke = @('--ssl-revoke-best-effort') }",
    "  Write-Host 'Downloading the LexSide installer...'",
    `  & curl.exe @revoke --fail --silent --show-error --location 'https://${checked(endpoints.installer, DNS_NAME, 'installer host')}/install.ps1' --output $f`,
    "  if ($LASTEXITCODE -ne 0 -or -not (Test-Path -LiteralPath $f)) { throw 'LexSide installer download failed.' }",
    `  & powershell -NoProfile -ExecutionPolicy Bypass -File $f ${argument}`,
    '  if ($LASTEXITCODE -ne 0) { exit $LASTEXITCODE }',
    '} finally {',
    '  Remove-Item -LiteralPath $f -Force -ErrorAction SilentlyContinue',
    '}',
  ].join('\r\n');
  return `powershell -NoProfile -ExecutionPolicy Bypass -EncodedCommand ${utf16LeBase64(bootstrap)}`;
}

/** A firm zone's install also names its topology and host (spec §3.10, reference design P5). */
export function windowsInstallCommand(claimToken: string, endpoints: {china: boolean;installer: string}, zoneHost?: string | null, shell: WindowsShell = 'powershell'): string {
  const args = installArguments(claimToken, zoneHost);
  return shell === 'cmd' ? cmdCommand(args, endpoints) : powershellCommand(endpoints.installer, args);
}

export function windowsUpgradeCommand(endpoints: {china: boolean;installer: string}, shell: WindowsShell = 'powershell'): string {
  return shell === 'cmd' ? cmdCommand('-Upgrade', endpoints) : powershellCommand(endpoints.installer, '-Upgrade');
}

export function macosUpgradeCommand(endpoints: {china: boolean;installer: string}): string {
  const overrides = endpoints.china
    ? 'LEXSIDE_CONTROL_URL=https://api.cn.lexside.ai LEXSIDE_APP_ORIGIN=https://cn.lexside.ai LEXSIDE_RELEASE_BASE_URL=https://dl.cn.lexside.ai/releases '
    : '';
  return `curl -fsSL https://${endpoints.installer}/install.sh | ${overrides}bash -s -- --upgrade`;
}
